// Audio journals: an issue's narrated audio published from the SAS Audio Journal tool.
// Contract: audio_journal/docs/sas-app-publishing.md
import { Article, AudioChapter, DownloadState, DownloadStatus, Edition, EditionAudio, Publication } from 'types';

export const AUDIO_JOURNAL_TYPE = 'audiojournal';
const PREFIX = 'aj-';
const RESUME_KEY = 'audioJournalResume';

export const audioJournalId = (editionId: string) => PREFIX + editionId;
export const isAudioJournalId = (id?: string) => !!id && id.startsWith(PREFIX);
export const editionIdOf = (id: string) => id.slice(PREFIX.length);
export const isAudioJournalArticle = (article?: Article) => article?.articleType === AUDIO_JOURNAL_TYPE;
/** Font class for an audio journal's own text (title, chapter titles), by the journal's language. */
export const audioJournalFont = (article?: Article) => (article?.lang === 'hi' ? 'locale-hi' : '');
/** The media link of an issue's audio (like /bhajan/{id}); opening it plays the issue. */
export const audioJournalPath = (editionId: string) => `/${AUDIO_JOURNAL_TYPE}/${audioJournalId(editionId)}`;
export const readAlongPath = (editionId: string) => `/publication/${editionId}`; // the issue's reader, which reads along

export function timeMs(value: any): number | undefined {
	if (!value) {
		return undefined;
	}
	if (typeof value.toMillis === 'function') {
		return value.toMillis();
	}
	if (typeof value.seconds === 'number') {
		return value.seconds * 1000 + Math.floor((value.nanoseconds ?? 0) / 1e6);
	}
	return undefined;
}

export type AudioStatus = 'draft' | 'scheduled' | 'published';

export function audioStatus(audio?: EditionAudio, now = Date.now()): AudioStatus | undefined {
	if (!audio) {
		return undefined;
	}
	if (audio.status !== 'published') {
		return 'draft';
	}
	const at = timeMs(audio.publishTime);
	return at !== undefined && at <= now ? 'published' : 'scheduled';
}

/** Listeners see published audio from its release time; admins and test users also see drafts. */
export function canListen(edition: Edition | undefined, privileged: boolean, now = Date.now()) {
	const status = audioStatus(edition?.audio, now);
	return !!status && !!edition?.audio?.listen?.file && (status === 'published' || privileged);
}

export function audioFileUrl(audio: EditionAudio, file: string) {
	return (process.env.REACT_APP_CDN_HOST_R2 ?? '') + encodeURI(`appstatic/${audio.path}/${file}`);
}

/** "Nov'26" for the issue's date in India: short enough for titles in lists and the player. */
export function issueShort(edition: Edition) {
	const ms = timeMs(edition.publishTime);
	if (ms === undefined) {
		return '';
	}
	const date = new Date(ms + 5.5 * 3600 * 1000);
	return `${date.toLocaleString('en', { month: 'short', timeZone: 'UTC' })}'${String(date.getUTCFullYear()).slice(2)}`;
}

/** The player item for an issue's audio: one continuous file with chapters by time. */
export function audioJournalArticle(edition: Edition, publication?: Publication, coverUrl?: string, uriOverride?: string): Article {
	const audio = edition.audio!;
	const uri = uriOverride ?? `${audio.path}/${audio.listen.file}`;
	const name = publication?.title ?? 'Audio journal';
	return {
		id: audioJournalId(edition.id),
		draftId: '',
		forDate: '',
		// Just the journal's name (tiles already show the issue's date), under the journal's language
		// only, as for other content, so lists pick the right font.
		title: { [audio.language]: name },
		articleType: AUDIO_JOURNAL_TYPE,
		mediaType: 'audio',
		mediaUri: uri,
		mediaUriR2: uri,
		mediaLength: audio.duration,
		author: 'SAS Audio Journal',
		group: name,
		lang: audio.language,
		active: true,
		deleted: false,
		publishTime: (audio.publishTime ?? edition.publishTime) as any,
		expireTime: edition.expireTime,
		createdAt: edition.createdAt,
		updatedAt: edition.updatedAt,
		audioJournal: {
			editionId: edition.id,
			publicationId: edition.publicationId,
			version: audio.version,
			issue: issueShort(edition),
			chapters: audio.chapters ?? [],
			syncFile: audio.sync?.file,
			coverUrl,
		},
	};
}

// ---------- playback speed (audio journals stop at 2×) ----------

export const MAX_AUDIO_JOURNAL_SPEED = 2;

/** The speed an audio journal plays at, given the listener's general speed setting. */
export function effectiveSpeed(speed?: number) {
	return Math.min(Math.max(speed || 1, 0.5), MAX_AUDIO_JOURNAL_SPEED);
}

// The app player keeps its speed between tracks, so after capping it for an audio journal the
// listener's own speed is put back when other audio starts.
let speedCapped = false;
export const markSpeedCapped = () => {
	speedCapped = true;
};
export function takeSpeedCapped() {
	const was = speedCapped;
	speedCapped = false;
	return was;
}

// ---------- chapters ----------

export function chapterIndexAt(chapters: AudioChapter[], time: number) {
	let index = 0;
	for (let i = 0; i < chapters.length; i++) {
		if (chapters[i].start <= time + 0.25) {
			index = i;
		}
	}
	return index;
}

/** Start of the current chapter, or of the previous one when already near the start. */
export function previousChapterTime(chapters: AudioChapter[], time: number) {
	if (!chapters.length) {
		return 0;
	}
	const index = chapterIndexAt(chapters, time);
	return time - chapters[index].start > 3 || index === 0 ? chapters[index].start : chapters[index - 1].start;
}

export function nextChapterTime(chapters: AudioChapter[], time: number): number | undefined {
	const index = chapterIndexAt(chapters, time);
	return index + 1 < chapters.length ? chapters[index + 1].start : undefined;
}

// ---------- resume (survives a corrected re-publish) ----------

export interface ResumePoint {
	version: string;
	articleId: string;
	offset: number;
}

export function resumePoint(audio: EditionAudio, time: number): ResumePoint {
	const chapters = audio.chapters ?? [];
	const chapter = chapters[chapterIndexAt(chapters, time)];
	return { version: audio.version, articleId: chapter?.articleId ?? '', offset: Math.max(0, time - (chapter?.start ?? 0)) };
}

/** Where to start: the saved position, mapped to the same chapter when the audio was re-published. */
export function resumeTime(audio: EditionAudio, played?: number, saved?: ResumePoint): number {
	let time = played ?? 0;
	if (saved && saved.version !== audio.version) {
		const chapter = (audio.chapters ?? []).find((c) => c.articleId === saved.articleId);
		time = chapter ? Math.min(chapter.start + saved.offset, Math.max(chapter.start, chapter.end - 2)) : 0;
	}
	return time > 5 && time < audio.duration - 5 ? time : 0;
}

function resumeStore(): { [editionId: string]: ResumePoint } {
	try {
		return JSON.parse(localStorage.getItem(RESUME_KEY) ?? '{}') ?? {};
	} catch {
		return {};
	}
}

export function loadResumePoint(editionId: string): ResumePoint | undefined {
	return resumeStore()[editionId];
}

export function saveResumePoint(editionId: string, point: ResumePoint) {
	try {
		localStorage.setItem(RESUME_KEY, JSON.stringify({ ...resumeStore(), [editionId]: point }));
	} catch {
		// Storage full or unavailable: resume falls back to the played time.
	}
}

/**
 * What to play and from where when an issue is started without an explicit position: the saved
 * position (mapped to the same chapter after a re-publish), and the saved offline file when the
 * device is offline and the issue has since been re-published.
 */
export function audioJournalPlayback(article: Article, played: number | undefined, saved: DownloadState | undefined, online: boolean) {
	const info = article.audioJournal!;
	const audio = { version: info.version, chapters: info.chapters, duration: article.mediaLength } as EditionAudio;
	const start = resumeTime(audio, played, loadResumePoint(info.editionId));
	const useSaved = !online && offlineCopy(saved, article) === 'outdated' && saved?.progress?.status === DownloadStatus.Complete;
	return { article: useSaved ? { ...article, mediaUri: saved!.uri, mediaUriR2: saved!.uri } : article, start };
}

// ---------- offline copies ----------

export type OfflineCopy = 'none' | 'downloading' | 'saved' | 'outdated';

/** A saved copy is outdated when the issue now points at a different (re-published) file. */
export function offlineCopy(entry: DownloadState | undefined, article: Article): OfflineCopy {
	if (!entry) {
		return 'none';
	}
	if (entry.uri !== article.mediaUri) {
		return 'outdated';
	}
	return entry.progress?.status === DownloadStatus.Complete ? 'saved' : 'downloading';
}

export function formatDuration(seconds: number) {
	const total = Math.max(0, Math.round(seconds));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

// ---------- push notifications (server-only records, managed through manageAudioJournalPush) ----------

// The app's notification topics, as named in admin (also used by the Notifications form).
export const PUSH_AUDIENCES = { all: 'All Users', all_android: 'Android Users', all_ios: 'iOS Users', test: 'Test Users' };
export type PushTopic = keyof typeof PUSH_AUDIENCES;

export interface AudioPush {
	id: string;
	title: string;
	text: string;
	topic: PushTopic;
	timing: 'release' | 'at';
	sendAt: string | null;
	image: string | null;
	status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
	waiting?: string;
	sentAt?: string | null;
	error?: string;
	reason?: string;
	createdBy?: string;
	createdAt?: string | null;
}

const at = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

// ---------- composing a push in admin ----------

export interface PushDraft {
	id?: string; // editing a scheduled push
	title: string;
	text: string;
	topic: string;
	timing: string; // 'release' | 'at'
	sendAt: number;
	image: boolean;
}

export function pushPayload(draft: PushDraft) {
	return {
		title: draft.title.trim(),
		text: draft.text.trim(),
		topic: draft.topic,
		timing: draft.timing,
		...(draft.timing === 'at' ? { at: new Date(draft.sendAt).toISOString() } : {}),
		image: draft.image,
	};
}

export function pushDraft(push: AudioPush): PushDraft {
	return {
		id: push.id,
		title: push.title,
		text: push.text,
		topic: push.topic,
		timing: push.timing,
		sendAt: push.sendAt ? Date.parse(push.sendAt) : Date.now() + 60 * 60 * 1000,
		image: !!push.image,
	};
}

/** One line describing where a push stands. */
export function pushStatusText(push: AudioPush, now = Date.now()) {
	switch (push.status) {
		case 'sent':
			return `Sent ${at(push.sentAt)}`;
		case 'sending':
			return 'Sending…';
		case 'failed':
			return `Failed: ${push.error ?? 'unknown error'}`;
		case 'cancelled':
			return push.reason === 'audio-removed' ? 'Cancelled (the audio was removed)' : 'Cancelled';
	}
	if (push.timing === 'at' && push.sendAt && Date.parse(push.sendAt) > now) {
		return `Scheduled for ${at(push.sendAt)}`;
	}
	return push.waiting === 'audio-not-published' ? 'Waiting for the audio to be released' : 'Sending within 5 minutes';
}
